/**
 * The chat's context, held while the page reads its data again (chat follow-up 1). A period change or Retry starts
 * the reads over and the page's context goes null until they settle; the bubble mounted only on a context, so it
 * unmounted, and an answer in flight was lost with its 2 credits already spent. While the context is null this
 * returns the last one given under the same scope, with `pending`, so the bubble stays mounted and takes no new
 * question until the new context arrives. A new scope drops the held one in the same render: it never crosses a scope.
 * A context that goes null with no read running (a period the page refuses) is held the same way, until a valid period
 * or a new scope: null vs ready only.
 * A ref, written in a layout effect: render reads only what a commit wrote, so a discarded render (e.g. a transition)
 * never changes the held context; a layout effect runs before the next render, so there is still no null commit. State
 * would cost a render per new context, and loop on a caller rebuilding it each render.
 *
 * @module hooks/useHeldChatContext
 */
import { useLayoutEffect, useRef } from 'react';

export interface HeldChatContext<T> {
  context: T | null;
  /** True only while the given context is null and a held one is returned. */
  pending: boolean;
}

export const useHeldChatContext = <T>(context: T | null, scope: string): HeldChatContext<T> => {
  const held = useRef<{ scope: string; context: T } | null>(null);
  useLayoutEffect(() => {
    if (context !== null) held.current = { scope, context };
    else if (held.current?.scope !== scope) held.current = null;
  }, [context, scope]);
  const kept = held.current?.scope === scope ? held.current.context : null;
  const shown = context !== null ? context : kept;
  return { context: shown, pending: context === null && shown !== null };
};
